import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Search, Check, Ban, Minus, ChevronDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { smartSortValues } from "@/lib/filterValues";
import {
  valueLabel, matchesSearch, selectAllVisible, clearVisible, invertVisible, visibleState,
} from "@/lib/filterSelection";

// Item 4 — the value picker shared by the per-widget "Filters on this visual" rows.
// Same engine as the standalone filter widgets: every edit is pending until Apply,
// Cancel/Esc restores the last applied values, Select all covers the visible rows only,
// and the include/exclude switch maps to the backend's in / not_in.
export function FilterValuePicker({ datasetId, field, value, exclude, onApply, disabled = false, testid = "wf-value-picker" }) {
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState(null);           // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [ex, setEx] = useState(!!exclude);
  const committed = useMemo(() => (Array.isArray(value) ? value.map((v) => String(v)) : []), [value]);

  useEffect(() => {
    if (!open) return;
    setSel(committed);
    setEx(!!exclude);
    setQ("");
    if (all || !datasetId || !field) return;
    setLoading(true);
    api.post(`/datasets/${datasetId}/distinct`, { field, filters: [], limit: 2000 })
      .then(({ data }) => setAll(smartSortValues((data.values || []).map((v) => (v == null ? "" : String(v))), "asc", "auto")))
      .catch(() => setAll([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, datasetId, field]);

  const values = all || [];
  const visible = q ? values.filter((v) => matchesSearch(v, q)) : values;
  const state = visibleState(sel, visible);
  const apply = () => { onApply(sel, ex); setOpen(false); };
  const cancel = () => { setSel(committed); setEx(!!exclude); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={(v) => (v ? setOpen(true) : cancel())}>
      <PopoverTrigger asChild>
        <button data-testid={testid} disabled={disabled || !field}
          className="h-8 w-full flex items-center justify-between gap-1 rounded-md border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-2 text-xs text-left disabled:opacity-50">
          <span className={cn("truncate", !committed.length && "text-[color:var(--ink-3)]")}>
            {committed.length ? `${exclude ? "Excluding " : ""}${committed.length === 1 ? valueLabel(committed[0]) : `${committed.length} values`}` : "Pick values…"}
          </span>
          <ChevronDown className="w-3 h-3 shrink-0 text-[color:var(--ink-3)]" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1.5" data-testid={`${testid}-popover`}
        onKeyDown={(e) => { if (e.key === "Escape") cancel(); }}>
        <div className="relative mb-1">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
          <Input data-testid={`${testid}-search`} autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search values…" className="h-7 pl-6 text-xs bg-[color:var(--surface-2)]" />
        </div>
        <div className="flex items-center gap-1 px-0.5 py-1 border-b border-[color:var(--glass-border)] text-[10px]">
          <button data-testid={`${testid}-all`} data-state={state} className="flex items-center gap-1 px-1 py-0.5 rounded hover:bg-[color:var(--surface-2)]"
            onClick={() => setSel(state === "all" ? clearVisible(sel, visible) : selectAllVisible(sel, visible))}>
            <span data-on={state === "all"} className={cn("w-3 h-3 rounded border flex items-center justify-center shrink-0",
              state === "all" ? "bg-[color:var(--brand-ocean)] border-[color:var(--brand-ocean)] text-white" : "border-[color:var(--glass-border)]")}>
              {state === "all" ? <Check className="w-2 h-2" /> : state === "some" ? <Minus className="w-2 h-2" /> : null}
            </span>
            Select all
          </button>
          <button data-testid={`${testid}-clear`} onClick={() => setSel([])} className="px-1 py-0.5 rounded hover:bg-[color:var(--surface-2)]">Clear</button>
          <button data-testid={`${testid}-invert`} onClick={() => setSel(invertVisible(sel, visible))} className="px-1 py-0.5 rounded hover:bg-[color:var(--surface-2)]">Invert</button>
          <button data-testid={`${testid}-exclude`} onClick={() => setEx((v) => !v)} data-on={ex}
            title={ex ? "Excluding the selected values (NOT IN)" : "Keeping only the selected values (IN)"}
            className={cn("ml-auto flex items-center gap-1 px-1 py-0.5 rounded hover:bg-[color:var(--surface-2)]", ex && "text-[color:var(--danger)] font-semibold")}>
            <Ban className="w-2.5 h-2.5" />{ex ? "Exclude" : "Include"}
          </button>
        </div>
        <div className="max-h-56 overflow-auto py-0.5" data-testid={`${testid}-list`}>
          {loading && <p className="text-[10px] text-[color:var(--ink-3)] px-2 py-2 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Loading values…</p>}
          {!loading && visible.slice(0, 500).map((v) => {
            const on = sel.includes(v);
            return (
              <button key={String(v)} data-testid={`${testid}-opt-${String(v)}`} data-on={on}
                onClick={() => setSel(on ? sel.filter((x) => x !== v) : [...sel, v])}
                className="w-full flex items-center gap-2 px-2 py-1 text-xs rounded-md text-left hover:bg-[color:var(--surface-2)]">
                <span className={cn("w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0",
                  on ? "bg-[color:var(--brand-ocean)] border-[color:var(--brand-ocean)] text-white" : "border-[color:var(--glass-border)]")}>
                  {on && <Check className="w-2.5 h-2.5" />}
                </span>
                <span className={cn("truncate", (String(v).trim() === "" || String(v).startsWith("(")) && "italic text-[color:var(--ink-3)]")}>{valueLabel(v)}</span>
              </button>
            );
          })}
          {!loading && !visible.length && <p className="text-[10px] text-[color:var(--ink-3)] px-2 py-2">No values match</p>}
        </div>
        <div className="flex items-center justify-between gap-1 pt-1 border-t border-[color:var(--glass-border)]">
          <span className="text-[9px] text-[color:var(--ink-3)] pl-1">{sel.length} selected</span>
          <span className="flex gap-1">
            <button data-testid={`${testid}-cancel`} onClick={cancel} className="px-2 py-0.5 rounded text-[10px] border border-[color:var(--glass-border)]">Cancel</button>
            <button data-testid={`${testid}-apply`} onClick={apply} className="px-2 py-0.5 rounded text-[10px] bg-[color:var(--brand-ocean)] text-white">Apply</button>
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
}
